import type { createApiClient } from '@/services/api';
import type { WorkoutAttribution } from '@ergshoot/shared';

type ApiClient = Pick<ReturnType<typeof createApiClient>, 'get' | 'post' | 'patch' | 'delete'>;

export interface AdminAffiliate {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  country_code: string | null;
  timezone: string | null;
  phone: string | null;
  whatsapp: string | null;
  contact_email: string | null;
  subscription_status: string;
  member_count: number;
  owner: { id: string; name: string; email: string };
  agent?: { id: string; name: string; territory_name: string | null };
  created_at: string;
}

export interface PendingAdminAffiliate {
  id: string;
  name: string;
  address: string | null;
  country_code: string | null;
  phone: string | null;
  whatsapp: string | null;
  contact_email: string | null;
  member_count: number;
  status: string;
  source: 'affiliate' | 'application';
  owner: { id: string; name: string; email: string };
  created_at: string;
}

export interface AdminWorkout {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment: string[];
  scheduled_date: string;
  track: { id: string; name: string | null } | null;
  created_at: string;
  created_by_username?: string | null;
  attribution?: WorkoutAttribution | null;
}

export interface AdminWorkoutInput {
  name: string;
  description?: string;
  movements?: string[];
  scoring_type: string;
  scoring_direction?: string;
  equipment?: string[];
  scheduled_date: string;
  track_id?: string;
  category?: string;
  workout_type?: string;
  coach_notes?: string;
  video_url?: string;
}

export interface AdminCreatedWorkout {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment: string[];
  scheduled_date: string;
  track_id: string | null;
  category: string | null;
  workout_type: string;
}

export type AdminWorkoutUpdate = Partial<AdminWorkoutInput>;

export function createAdminAffiliateApi(client: ApiClient) {
  return {
    getPendingAffiliates: () =>
      client.get<{
        pending_count: number;
        affiliates: PendingAdminAffiliate[];
      }>('/admin/affiliates/pending'),

    createAffiliate: (data: {
      name: string;
      address?: string;
      phone?: string;
      email?: string;
      country_code?: string;
      agent_id?: string;
    }) =>
      client.post<{
        affiliate: {
          id: string;
          name: string;
          address: string | null;
          phone: string | null;
          agent_id: string | null;
          subscription_status: string;
        };
        message: string;
      }>('/admin/affiliates', data),

    updateAffiliate: (
      affiliateId: string,
      data: {
        name?: string;
        address?: string;
        phone?: string;
        whatsapp?: string;
        contact_email?: string;
        country_code?: string;
        agent_id?: string | null;
        owner_user_id?: string | null;
      }
    ) =>
      client.patch<{
        affiliate: { id: string; name: string };
        message: string;
      }>(`/admin/affiliates/${affiliateId}`, data),

    deleteAffiliate: (affiliateId: string) =>
      client.post<{ message: string; has_active_subscription?: boolean }>(
        `/admin/affiliates/${affiliateId}/delete`,
        { confirmation: 'DELETE' }
      ),

    approveAffiliate: (affiliateId: string, agentId?: string) =>
      client.patch<{
        affiliate: { id: string; status: string; agent_id?: string };
        message: string;
      }>(`/admin/affiliates/${affiliateId}/approve`, agentId ? { agent_id: agentId } : undefined),

    rejectAffiliate: (affiliateId: string, reason?: string) =>
      client.patch<{
        affiliate: { id: string; status: string };
        message: string;
        clerk_org_deleted?: boolean;
      }>(`/admin/affiliates/${affiliateId}/reject`, { confirmation: 'DELETE', reason }),

    getAffiliates: (params?: {
      status?: string;
      search?: string;
      cursor?: string;
      limit?: number;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.set('status', params.status);
      if (params?.search) queryParams.set('search', params.search);
      if (params?.cursor) queryParams.set('cursor', params.cursor);
      if (params?.limit) queryParams.set('limit', params.limit.toString());
      const query = queryParams.toString();
      return client.get<{
        affiliates: AdminAffiliate[];
        counts?: {
          total: number;
          pending: number;
          active: number;
          cancelled: number;
          past_due: number;
        };
        pagination: { has_more: boolean; next_cursor: string | null };
      }>(`/admin/affiliates${query ? `?${query}` : ''}`);
    },

    suspendAffiliate: (affiliateId: string, reason?: string) =>
      client.patch<{
        affiliate: { id: string; status: string };
        message: string;
      }>(`/admin/affiliates/${affiliateId}/suspend`, { reason }),

    activateAffiliate: (affiliateId: string) =>
      client.patch<{
        affiliate: { id: string; name: string; previous_status: string; status: string };
        message: string;
      }>(`/admin/affiliates/${affiliateId}/activate`),
  };
}

export function createAdminProgrammingApi(client: ApiClient) {
  return {
    getProgramming: (params?: {
      track_id?: string;
      cursor?: string;
      limit?: number;
      search?: string;
      category?: string;
      scoring_type?: string;
      date_from?: string;
      date_to?: string;
      equipment?: string;
      workout_type?: string;
      source?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.track_id) queryParams.set('track_id', params.track_id);
      if (params?.cursor) queryParams.set('cursor', params.cursor);
      if (params?.limit) queryParams.set('limit', params.limit.toString());
      if (params?.search) queryParams.set('search', params.search);
      if (params?.category) queryParams.set('category', params.category);
      if (params?.scoring_type) queryParams.set('scoring_type', params.scoring_type);
      if (params?.date_from) queryParams.set('date_from', params.date_from);
      if (params?.date_to) queryParams.set('date_to', params.date_to);
      if (params?.equipment) queryParams.set('equipment', params.equipment);
      if (params?.workout_type) queryParams.set('workout_type', params.workout_type);
      if (params?.source) queryParams.set('source', params.source);
      const query = queryParams.toString();
      return client.get<{
        workouts: AdminWorkout[];
        pagination: { has_more: boolean; next_cursor: string | null };
      }>(`/admin/programming${query ? `?${query}` : ''}`);
    },

    createWorkout: (data: AdminWorkoutInput, idempotencyKey?: string) =>
      client.post<{
        workout: AdminCreatedWorkout;
        message: string;
      }>('/admin/programming', data, {
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      }),

    updateWorkout: (workoutId: string, data: AdminWorkoutUpdate, idempotencyKey?: string) =>
      client.patch<{ message: string }>(`/admin/programming/${workoutId}`, data, {
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      }),

    deleteWorkout: (workoutId: string, force = false) =>
      client.delete<{ message: string }>(
        `/admin/programming/${workoutId}${force ? '?force=true' : ''}`
      ),

    bulkDeleteWorkouts: (ids: string[], force = false) =>
      client.post<{
        deleted: number;
        skipped: number;
        errors: number;
        details: {
          deleted: string[];
          skipped: Array<{ id: string; reason: string }>;
          errors: Array<{ id: string; error: string }>;
        };
        message: string;
      }>('/admin/programming/bulk-delete', { ids, force }),

    bulkUploadWorkouts: (csv: string, trackId?: string) =>
      client.post<AdminProgrammingUploadResult>('/admin/programming/bulk-upload', {
        csv,
        track_id: trackId,
      }),

    bulkUploadStandards: (csv: string) =>
      client.post<AdminProgrammingUploadResult>('/admin/programming/bulk-upload-standards', {
        csv,
      }),

    bulkUploadSpecialty: (csv: string, trackId: string) =>
      client.post<AdminProgrammingUploadResult>('/admin/programming/bulk-upload-specialty', {
        csv,
        track_id: trackId,
      }),

    getTracks: () =>
      client.get<{
        tracks: Array<{
          id: string;
          name: string;
          description: string | null;
          is_active: boolean;
          is_coming_soon: boolean;
          logo_url: string | null;
          access_level: string;
          workout_count: number;
          created_at: string;
        }>;
      }>('/admin/tracks'),

    deleteTrack: (trackId: string) =>
      client.delete<{
        deleted: { id: string; name: string };
        message: string;
      }>(`/admin/tracks/${trackId}`),
  };
}

export interface AdminProgrammingUploadResult {
  created: number;
  errors: number;
  total_rows: number;
  created_workouts: Array<{ row: number; name: string; id: string }>;
  row_errors: Array<{ row: number; error: string }>;
  message: string;
}
